import { cleanFloat } from "@/lib/tool-kit/grades/rounding";

/**
 * French /20 marks → US letter grades and GPA, and French mention bands.
 *
 * There is no official French-to-US conversion. These are three tables published by
 * institutions that handle French students, so a reader can pick the one that matches the
 * university evaluating them. Retrieved October 2026:
 *
 * - "sciences-po": Sciences Po Strasbourg, "Grading and Transcripts" (corrected 14 December
 *   2023), https://www.sciencespo-strasbourg.fr/websites/sciencespo/images/Grading_GradeConversion_InterpretingGradeReports_SciencesPoStrasbourg_Corr_2023.12.14.pdf
 *   Its module-by-module table (14–20 A 4.0; 12–13.99 B+ 3.33; 11–11.99 B 3.0; 10.5–10.99 B−
 *   2.7; 10.1–10.49 C+ 2.33; 10 C 2.0; 8–9.99 D 1.0; 0–7.99 F) is used for single marks, and its
 *   global conversion for a yearly average, which has no D (below 10 is F).
 * - "ncsu": NC State University Study Abroad, "Europe Grade Equivalencies", the France table
 *   (14–20 A; 12–13.9 B+; 11–11.9 B; 10.5–10.9 B−; 10.1–10.4 C+; 10 C; 9–9.9 C−; 8–8.9 D;
 *   0–7.9 F), https://studyabroad.ncsu.edu/europe-grade-equivalencies/. The page gives letters
 *   only, so the grade points are the common US plus/minus values (A 4.0, B+ 3.3, B 3.0 …).
 * - "ags": American Graduate School in Paris, "Grading Scale", the French system equivalents
 *   for exchange students (17–20 A 4.0; 16 A− 3.7; 15 B+ 3.3; 14 B 3.0; 13 B− 2.7; 12 C+ 2.3;
 *   11 C 2.0; 10 C− 1.7; 6–9 D 1.0; 0–5 F),
 *   https://www.ags.edu/international-relations/current-students/academic-info/grading-scale.
 *   The table lists whole numbers, so a mark such as 14.5 is read in the band of the whole
 *   number below it — an assumption, stated on the page.
 *
 * A mark takes the band whose lower bound it meets, so 10.05 on the Sciences Po and NC State
 * tables is still a C (both tables jump from 10 to 10.1).
 *
 * The mention bands are the honours scale Sciences Po Strasbourg gives "along the following
 * scale (general French use)" in the same document: below 10 ajourné; 10–11.99 passable;
 * 12–13.99 assez bien; 14–15.99 bien; 16–20 très bien. 10/20 is the pass mark there and at
 * UPHF (Université Polytechnique Hauts-de-France, "Grades and grading scale system").
 */

export type FrenchScale = "sciences-po" | "ncsu" | "ags";

export const FRENCH_SCALES: readonly FrenchScale[] = [
  "sciences-po",
  "ncsu",
  "ags",
];

export interface FrenchBand {
  /** The lowest mark, inclusive, that earns this band. */
  min: number;
  letter: string;
  points: number;
}

const SCIENCES_PO_MODULE: readonly FrenchBand[] = [
  { min: 14, letter: "A", points: 4 },
  { min: 12, letter: "B+", points: 3.33 },
  { min: 11, letter: "B", points: 3 },
  { min: 10.5, letter: "B−", points: 2.7 },
  { min: 10.1, letter: "C+", points: 2.33 },
  { min: 10, letter: "C", points: 2 },
  { min: 8, letter: "D", points: 1 },
  { min: 0, letter: "F", points: 0 },
];

const SCIENCES_PO_GLOBAL: readonly FrenchBand[] = [
  { min: 14, letter: "A", points: 4 },
  { min: 12, letter: "B+", points: 3.33 },
  { min: 11, letter: "B", points: 3 },
  { min: 10.5, letter: "B−", points: 2.7 },
  { min: 10.1, letter: "C+", points: 2.33 },
  { min: 10, letter: "C", points: 2 },
  { min: 0, letter: "F", points: 0 },
];

const NCSU: readonly FrenchBand[] = [
  { min: 14, letter: "A", points: 4 },
  { min: 12, letter: "B+", points: 3.3 },
  { min: 11, letter: "B", points: 3 },
  { min: 10.5, letter: "B−", points: 2.7 },
  { min: 10.1, letter: "C+", points: 2.3 },
  { min: 10, letter: "C", points: 2 },
  { min: 9, letter: "C−", points: 1.7 },
  { min: 8, letter: "D", points: 1 },
  { min: 0, letter: "F", points: 0 },
];

const AGS: readonly FrenchBand[] = [
  { min: 17, letter: "A", points: 4 },
  { min: 16, letter: "A−", points: 3.7 },
  { min: 15, letter: "B+", points: 3.3 },
  { min: 14, letter: "B", points: 3 },
  { min: 13, letter: "B−", points: 2.7 },
  { min: 12, letter: "C+", points: 2.3 },
  { min: 11, letter: "C", points: 2 },
  { min: 10, letter: "C−", points: 1.7 },
  { min: 6, letter: "D", points: 1 },
  { min: 0, letter: "F", points: 0 },
];

/** The band table for single marks and for an average. They differ only for Sciences Po. */
export function frenchBands(
  scale: FrenchScale,
  kind: "mark" | "average"
): readonly FrenchBand[] {
  if (scale === "sciences-po") {
    return kind === "mark" ? SCIENCES_PO_MODULE : SCIENCES_PO_GLOBAL;
  }
  return scale === "ncsu" ? NCSU : AGS;
}

export const FRENCH_MAX_MARK = 20;
export const FRENCH_PASS_MARK = 10;

export function isValidFrenchMark(mark: number): boolean {
  return Number.isFinite(mark) && mark >= 0 && mark <= FRENCH_MAX_MARK;
}

/** The band a mark falls in; null for a mark outside 0–20. */
export function convertFrenchMark(
  mark: number,
  scale: FrenchScale,
  kind: "mark" | "average" = "mark"
): FrenchBand | null {
  if (!isValidFrenchMark(mark)) {
    return null;
  }
  return frenchBands(scale, kind).find((band) => mark >= band.min) ?? null;
}

export type FrenchMention =
  | "ajourne"
  | "passable"
  | "assez-bien"
  | "bien"
  | "tres-bien";

/** Mention for an average out of 20 (Sciences Po Strasbourg's "general French use" scale). */
export function frenchMention(average: number): FrenchMention | null {
  if (!isValidFrenchMark(average)) {
    return null;
  }
  if (average >= 16) {
    return "tres-bien";
  }
  if (average >= 14) {
    return "bien";
  }
  if (average >= 12) {
    return "assez-bien";
  }
  return average >= 10 ? "passable" : "ajourne";
}

export interface FrenchCourse {
  mark: number;
  /** Credits or any other weight; must be above zero. */
  weight: number;
}

export interface FrenchSummary {
  /** Weight-averaged mark out of 20. */
  average: number;
  /** Weight-averaged grade points of each course's own conversion. */
  gpa: number;
  /** Conversion of the average mark itself, as the scale's global table reads it. */
  averageBand: FrenchBand;
  mention: FrenchMention;
  totalWeight: number;
}

/** Null when there is no valid course, or the weights do not add to a positive number. */
export function summariseFrench(
  courses: readonly FrenchCourse[],
  scale: FrenchScale
): FrenchSummary | null {
  let totalWeight = 0;
  let markSum = 0;
  let pointSum = 0;
  for (const course of courses) {
    const band = convertFrenchMark(course.mark, scale);
    if (band && course.weight > 0) {
      totalWeight += course.weight;
      markSum += course.mark * course.weight;
      pointSum += band.points * course.weight;
    }
  }
  if (totalWeight <= 0) {
    return null;
  }
  const average = cleanFloat(markSum / totalWeight);
  const averageBand = convertFrenchMark(average, scale, "average");
  const mention = frenchMention(average);
  if (!(averageBand && mention)) {
    return null;
  }
  return {
    average,
    gpa: cleanFloat(pointSum / totalWeight),
    averageBand,
    mention,
    totalWeight,
  };
}
